import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { config } from '../config';

interface User {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  tenantId: string;
  roles: string[];
  permissions: string[];
  isSuperAdmin?: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string, tenantSlug?: string) => Promise<void>;
  loginWithMagicLink: (token: string) => Promise<void>;
  requestMagicLink: (email: string, tenantSlug?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchCurrentUser = useCallback(async () => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${config.apiUrl}/auth/me`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const userData = data.data;
        setUser({
          id: userData.id,
          email: userData.email,
          name: userData.name,
          avatar: userData.avatar,
          tenantId: userData.tenant?.id || '',
          roles: userData.roles?.map((r: { name: string }) => r.name) || [],
          permissions: userData.permissions || [],
          isSuperAdmin: userData.roles?.some((r: { name: string }) => r.name === 'super_admin'),
        });
      } else if (response.status === 401) {
        // Try to refresh token
        await refreshToken();
      } else {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
      }
    } catch (error) {
      console.error('Failed to fetch current user:', error);
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  const login = async (email: string, password: string, tenantSlug?: string) => {
    const response = await fetch(`${config.apiUrl}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password, tenantSlug }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || 'Login failed');
    }

    localStorage.setItem('accessToken', data.data.accessToken);
    localStorage.setItem('refreshToken', data.data.refreshToken);

    setUser({
      id: data.data.user.id,
      email: data.data.user.email,
      name: data.data.user.name,
      avatar: data.data.user.avatar,
      tenantId: data.data.user.tenantId,
      roles: data.data.user.roles,
      permissions: data.data.user.permissions,
      isSuperAdmin: data.data.user.roles?.includes('super_admin'),
    });
  };

  const requestMagicLink = async (email: string, tenantSlug?: string) => {
    const response = await fetch(`${config.apiUrl}/auth/magic-link`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, tenantSlug }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || 'Failed to send magic link');
    }
  };

  const loginWithMagicLink = async (token: string) => {
    const response = await fetch(`${config.apiUrl}/auth/magic-link/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error?.message || 'Invalid or expired magic link');
    }

    localStorage.setItem('accessToken', data.data.accessToken);
    localStorage.setItem('refreshToken', data.data.refreshToken);

    setUser({
      id: data.data.user.id,
      email: data.data.user.email,
      name: data.data.user.name,
      avatar: data.data.user.avatar,
      tenantId: data.data.user.tenantId,
      roles: data.data.user.roles,
      permissions: data.data.user.permissions,
      isSuperAdmin: data.data.user.roles?.includes('super_admin'),
    });
  };

  const logout = async () => {
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken) {
      try {
        await fetch(`${config.apiUrl}/auth/logout`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
      } catch (error) {
        console.error('Logout error:', error);
      }
    }

    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setUser(null);
  };

  const refreshToken = async () => {
    const storedRefreshToken = localStorage.getItem('refreshToken');
    if (!storedRefreshToken) {
      throw new Error('No refresh token');
    }

    const response = await fetch(`${config.apiUrl}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken: storedRefreshToken }),
    });

    const data = await response.json();

    if (!response.ok) {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      setUser(null);
      throw new Error(data.error?.message || 'Token refresh failed');
    }

    localStorage.setItem('accessToken', data.data.accessToken);
    await fetchCurrentUser();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        loginWithMagicLink,
        requestMagicLink,
        logout,
        refreshToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
